import React, { useState, useContext, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useResolvedPath } from "react-router";
import { Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import MUIButton from "./../UI/MUIButton/MUIButton";
import Rankings from "./Rankings";
import SentimentTable from "./SentimentTable";
import { useReactToPrint } from "react-to-print";
import DownloadIcon from "@mui/icons-material/Download";
import classes from "./BrandIndexBreakups.module.css";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import { sanitizeObject, transformDriverData } from "../../utils/helpers";
import api from "../../BrandOnBoarding/api";
import SentimentDriversTable from "./SentimentDrivers";
import Spinner from "../Spinner/Spinner";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function BrandIndexBreakups({
  type,
  data,
  isMonitoring,
  brandProduct,
  isOpen,
  remountChild,
}) {
  const { pathname } = useResolvedPath();
  let selectedOptionShow = pathname.includes("brand-monitoring")
    ? "Brand"
    : "Product";
  const discoveryCtx = useContext(DiscoveryContext);
  const [savedMatchIndices, setSaveMatchIndices] = useState([]);
  const [saveReachScore, setSavedReachScore] = useState();
  const [userMatchedIndices, setUserMatchedIndices] = useState([]);
  const [userReachScore, setUserReachScore] = useState();
  const [loadingUpdatedRank, setLoadingUpdatedRank] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sentimentDrivers, setSentimentDrivers] = useState(
    data?.sentiementKeyDrivers
  );
  const [isDriversLodaing, setIsDriversLoading] = useState(false);
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

   async function updateRankScore(data) {
    try {
         const response = await api.patch(
        `${webSocketBaseURL}/transaction-request-update`,data
      );
      if (response.status !== 200) {
        toast.error("Failed to update discovery");
      }
    } catch (error) {
      toast.error("Failed to update discovery");
      throw error;
    }
  }

  const handleSentimentDrivers = async () => {
    const payload = {
      sentimentMeta: data?.sentimentMeta,
    };
    try {
      setIsDriversLoading(true);
      const response = await api.post("/metrics/sentiment-drivers", payload);
      setSentimentDrivers(response.data);
    } catch (err) {
    } finally {
      setIsDriversLoading(false);
    }
  };

  const stateBrandScoreRef = useRef();

  useEffect(() => {
    if (!isOpen) {
      handleRankReset();
    }
  }, [isOpen]);

  function handleRankChange(data) {
    const { index, name, pIndex } = data;
    let updatedBrandScores = [...discoveryCtx.brand_scores];
    const brandItemIndex = updatedBrandScores.findIndex(
      (item) => item.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
    let rankMatchesIndices =
      updatedBrandScores[brandItemIndex]?.extras?.rankMatchesIndices;

    if (brandItemIndex > -1) {
      let indiceItem = rankMatchesIndices.find(
        (item) =>
          item.name.toString().toLowerCase().trim() ===
          name.toString().toLowerCase().trim()
      );
      if (!indiceItem) {
        indiceItem = {
          name,
          indices: [-1, -1, -1, -1, -1],
        };
        rankMatchesIndices.push(indiceItem);
      }
      if (savedMatchIndices.length === 0) {
        setSavedReachScore(updatedBrandScores[brandItemIndex].rank);
        setSaveMatchIndices([...indiceItem.indices]);
      }
      indiceItem.indices[pIndex] = index;
      setUserMatchedIndices(indiceItem.indices);
      updatedBrandScores = updatedBrandScores.map((score) => {
        return {
          ...score,
          extras: {
            ...score.extras,
            rankMatchesIndices: rankMatchesIndices,
          },
        };
      });
    }
    discoveryCtx.replaceBrandScores(updatedBrandScores);
  }

  function handleRankReset() {
    if (saveReachScore === undefined || saveReachScore === null) return;
    const name = data.rank.length > 0 ? data.rank[0]?.name : "";
    const indices = structuredClone(
      discoveryCtx.brand_scores[0].extras.rankMatchesIndices
    );
    const brandProductIndiceItemIndex = indices.findIndex(
      (item) =>
        item.name.toString().toLowerCase().trim() ===
        name.toString().toLowerCase().trim()
    );
    if (brandProductIndiceItemIndex > -1) {
      indices[brandProductIndiceItemIndex] = {
        ...indices[brandProductIndiceItemIndex],
        indices: savedMatchIndices,
      };
    }
    const updatedBrandScores = discoveryCtx.brand_scores.map((brand_score) => {
      const updatedRankArr = structuredClone(brand_score.extras.rank);
      const rankIndex = updatedRankArr.findIndex(
        (item) => item.name.toString().trim() === name.toString().trim()
      );
      let originalRankScore = saveReachScore;
      if (rankIndex > -1) {
        updatedRankArr[rankIndex].score = saveReachScore;
      } else {
        originalRankScore = brand_score.rank;
      }
      return {
        ...brand_score,
        rank: originalRankScore,
        total: originalRankScore + brand_score.favorability,
        extras: {
          ...brand_score.extras,
          rank: updatedRankArr,
          rankMatchesIndices: indices,
        },
      };
    });
    discoveryCtx.replaceBrandScores(updatedBrandScores);
    setUserReachScore();
    setSavedReachScore();
    setSaveMatchIndices([]);
    setUserMatchedIndices([]);
  }

  const isIndicesSame = isBothIndicesSame(
    savedMatchIndices,
    userMatchedIndices
  );

  async function handleRankSave() {
    try {
      setIsLoading(true);
      if (!isIndicesSame) {
        await handleRankScoreRefresh();
      }
      const name = data.rank.length > 0 ? data.rank[0]?.name?.toString().trim() : "";

      const updatedDiscoveryState = {
        ...discoveryCtx.state,
        brand_scores: stateBrandScoreRef.current,
      };

      const rankdata = updatedDiscoveryState.brand_scores.find(
        (item) => item.name?.toString().trim() === name
      );

      if (rankdata) {
        const rankobj = rankdata.extras?.rank?.[0] || {};
        const rankScore = updatedDiscoveryState.rank_scores;
        if (Array.isArray(rankScore?.data)) {
          rankScore.data = rankScore.data.map((item) =>
            item.name === rankdata.name ? { ...item, ...rankobj } : item
          );
        }

        if (Array.isArray(rankScore?.extras?.rank)) {
          rankScore.extras.rank = rankScore.extras.rank.map((item) =>
            item.name === rankdata.name ? { ...item, ...rankobj } : item
          );
        }
        if (rankScore) {
          const { request_id, ...rankScoreData } = rankScore;
          const payload = {
            transaction_id: discoveryCtx.state.transactionId,
            request_id,
            rank_score: rankScoreData,
          };

          await updateRankScore(payload);
          setUserReachScore();
          setSavedReachScore();
          setSaveMatchIndices([]);
          setUserMatchedIndices([]);
          remountChild();
        }
      } else {
        console.warn("No matching rankdata found");
      }
    } catch (error) {
      console.log("An error occurred");
    } finally {
      setIsLoading(false);
    }
  }

  function isBothIndicesSame(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => item === arr2[index]);
  }

  async function handleRankScoreRefresh() {
    try {
      setLoadingUpdatedRank(true);
      const name = data.rank.length > 0 ? data.rank[0]?.name : "";
      const response = await api.post("/metrics/generate-rank-score", {
        indices: userMatchedIndices,
        name,
      });
      const rankScore = response.data.rankingObject[name] || 0;
      const updatedBrandScores = discoveryCtx.brand_scores.map(
        (brand_score) => {
          const updatedRankArr = structuredClone(brand_score.extras.rank);
          const rankIndex = updatedRankArr.findIndex(
            (item) => item.name.toString().trim() === name.toString().trim()
          );
          if (rankIndex > -1) {
            updatedRankArr[rankIndex].score = rankScore;
          }
          setUserReachScore(rankScore);
          if (
            name.toString().toLowerCase().trim() ===
            brand_score.name.toString().toLowerCase().trim()
          ) {
            return {
              ...brand_score,
              rank: rankScore,
              total: parseInt(rankScore) + brand_score.favorability,
              extras: {
                ...brand_score.extras,
                rank: updatedRankArr,
              },
            };
          } else {
            return brand_score;
          }
        }
      );
      stateBrandScoreRef.current = updatedBrandScores;
      discoveryCtx.replaceBrandScores(updatedBrandScores);
    } catch (error) {
      throw error;
    } finally {
      setLoadingUpdatedRank(false);
    }
  }

  const pdfRef = useRef();

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    // documentTitle: `${pdfTitle}`,
    documentTitle: `xyz`,
    removeAfterPrint: true,
    pageStyle: `
          @page {
            size: 380mm 200mm;
            margin-left: 10mm;
            margin-right: 10mm;
            margin-bottom: 5mm;
            margin-top: 15mm;
          }
          @media print {
          .parent-container {
      overflow: hidden;
    }
            body {
              -webkit-print-color-adjust: exact; /* Ensures accurate color printing */
            }
            .page-footer {
              position: fixed;
              bottom: 10mm;
              left: 10mm;
              right: 10mm;
              font-size: 20pt;
              color: #777;
              text-align: center;
            }
          }
        `,
  });

  if (pathname.includes("discovery")) {
    selectedOptionShow = type
      ? `${type.charAt(0).toUpperCase()}${type.slice(1)}`
      : selectedOptionShow;
  }

  return (
    <>
      <h3>
        {`${selectedOptionShow} Index Score`}
      </h3>
      {
        <Typography variant="button" color="textSecondary">
          {data?.categoryName}
        </Typography>
      }

      {isMonitoring && (
        <p style={{ color: "gray", fontStyle: "italic", marginTop: "10px" }}>
          Note: The displayed metadata is from one of the datasets in the
          selected timeframe, while the scores on the UI represent the average
          of the same timeframe.
        </p>
      )}
      <hr />
      <div className="mx-4">
        <TableContainer>
          <div className="d-flex justify-content-between align-items-center">
            <h5>1. Visibility</h5>
            {!isIndicesSame && (
              <div className={classes.actions}>
                <MUIButton
                  variant="outlined"
                  color="error"
                  disabled={isLoading}
                  onClick={handleRankReset}
                >
                  Discard
                </MUIButton>
                <MUIButton loading={isLoading} onClick={handleRankSave}>
                  Save
                </MUIButton>
              </div>
            )}
          </div>
          <hr />
          {data?.productRankingsArray && (
            <div className="container" id="reach_id">
              <div className="row">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <h6>A. Visibility Data</h6>
                  <DownloadIcon
                    onClick={exportToPDF}
                    style={{ cursor: "pointer" }}
                  />
                </div>

                <hr />
                <div style={{ display: "flex" }} id="print-pdf" ref={pdfRef}>
                  <Rankings
                    onRankChange={handleRankChange}
                    rankings={data?.productRankingsArray}
                    rankMatchesIndices={data?.rankMatchesIndices}
                    name={
                      data?.favorabilities?.length > 0
                        ? data.favorabilities[0]?.name
                        : ""
                    }
                  />
                </div>
                <hr />
              </div>
            </div>
          )}
          <h6>
            {data?.productRankingsArray ? "B" : "A"}. Average Visibility Scores
          </h6>
          <hr />
          <Table sx={{ minWidth: 650 }} size="medium" aria-label="simple table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell align="right">Score</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data?.rank && data?.rank?.length > 0
                ? data?.rank.length === 1
                  ? data?.rank?.map((row) => (
                    <StyledTableRow
                      key={row.name}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <StyledTableCell component="th" scope="row">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <span>{row.score}</span>
                        {!isIndicesSame && (
                          <RefreshIcon
                            onClick={handleRankScoreRefresh}
                            className={
                              loadingUpdatedRank
                                ? classes.rotate
                                : classes.update_rank
                            }
                          />
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                  : data?.rank
                    ?.filter((item) => item.name === brandProduct)
                    ?.map((row) => (
                      <StyledTableRow
                        key={row.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StyledTableCell component="th" scope="row">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.score}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                : null}
            </TableBody>
          </Table>
          <hr />
          <h5>2. Sentiment</h5>
          <hr />
          <SentimentTable data={data} />
          <hr />
          <div style={{ display: "flex", alignItems: "center" }}>
            <h5>3. Sentiment Drivers</h5> &nbsp; &nbsp;
            {!sentimentDrivers ? (
              !isDriversLodaing ? (
                <MUIButton
                  onClick={handleSentimentDrivers}
                  variant="outlined"
                  // className={classes.regenerate}
                  startIcon={<AutoAwesomeOutlinedIcon />}
                >
                  Generate
                </MUIButton>
              ) : (
                <Spinner />
              )
            ) : null}
          </div>
          {sentimentDrivers && Object.keys(sentimentDrivers).length > 0 && (
            <>
              {/* <hr />
                <h5>3. Sentiment Drivers</h5> */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
                id="sentimentdrivers_id"
              >
                {sentimentDrivers?.positive &&
                  sentimentDrivers?.positive.length > 0 && (
                    <div style={{ flex: 1, minWidth: "40%" }} >
                      <SentimentDriversTable
                        data={transformDriverData(
                          sentimentDrivers?.positive,
                          "Positive"
                        )}
                        title="Positive Sentiment Drivers"
                        id="positive_sentimentdrivers_id"
                      />
                    </div>
                  )}

                {sentimentDrivers?.negative &&
                  sentimentDrivers?.negative.length > 0 && (
                    <div style={{ flex: 1, minWidth: "40%" }} >
                      <SentimentDriversTable
                        data={transformDriverData(
                          sentimentDrivers?.negative,
                          "Negative"
                        )}
                        title="Negative Sentiment Drivers"
                        id="negative_sentimentdrivers_id"
                      />
                    </div>
                  )}
              </div>
            </>
          )}
        </TableContainer>
      </div>
    </>
  );
}
